// src/pages/groups/GroupQuizzesTab.jsx
import { useCallback, useEffect, useState } from "react";
import {
  BarChart3,
  ChevronDown,
  ChevronUp,
  Copy,
  Download,
  Eye,
  EyeOff,
  Link2,
  ListChecks,
  Lock,
  Pencil,
  Plus,
  RefreshCw,
  RotateCcw,
  Share2,
  Trash2,
} from "lucide-react";
import { quizService } from "../../services/quizService";
import { getErrorMessage, groupService } from "../../services/groupService";
import { Avatar, ConfirmDialog, EmptyState, Modal, Spinner, formatRelative } from "./groupsShared";
import QuizImportModal, { QuestionDetails } from "./QuizImportModal";
import RichText from "./RichText";
import { exportQuizResultsToExcel } from "../../utils/exportQuizResults";

const STATUS_LABELS = { NOT_STARTED: "لم يبدأ", IN_PROGRESS: "قيد التنفيذ", SUBMITTED: "تم التسليم" };
const REASON_LABELS = { MANUAL: "سلّمه الطالب بنفسه", TIME_UP: "انتهى الوقت", APP_LEFT: "غادر التطبيق" };

/* ───────────────────────── إنشاء / تعديل اختبار ───────────────────────── */

function QuizFormModal({ groupId, quiz, onClose, onSaved }) {
  const isEdit = !!quiz;
  const [form, setForm] = useState({
    title: quiz?.title || "",
    description: quiz?.description || "",
    duration_minutes: quiz?.duration_minutes ?? 30,
    questions_per_attempt: quiz?.questions_per_attempt ?? "",
    max_attempts: quiz?.max_attempts ?? 1,
    is_open: quiz?.is_open ?? true,
    shuffle_questions: quiz?.shuffle_questions ?? true,
    show_answers_after_submit: quiz?.show_answers_after_submit ?? false,
    requires_academic_id: quiz?.requires_academic_id ?? false,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const set = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async () => {
    if (!form.title.trim()) return setError("أدخل اسم الاختبار");
    if (!Number(form.duration_minutes) || Number(form.duration_minutes) < 1)
      return setError("يجب ألا تقل مدة الاختبار عن دقيقة واحدة");
    const attempts = Number(form.max_attempts);
    if (!Number.isInteger(attempts) || attempts < 1 || attempts > 50)
      return setError("يجب أن يكون عدد المحاولات بين 1 و50");

    const payload = {
      title: form.title.trim(),
      description: form.description.trim(),
      duration_minutes: Number(form.duration_minutes),
      questions_per_attempt: form.questions_per_attempt === "" ? null : Number(form.questions_per_attempt),
      max_attempts: attempts,
      is_open: form.is_open,
      shuffle_questions: form.shuffle_questions,
      show_answers_after_submit: form.show_answers_after_submit,
      requires_academic_id: form.requires_academic_id,
    };
    if (!isEdit) payload.group_ids = [Number(groupId)];

    setSaving(true);
    setError("");
    try {
      const res = isEdit ? await quizService.update(quiz.id, payload) : await quizService.create(payload);
      onSaved(res.data, res.message);
    } catch (e) {
      setError(getErrorMessage(e));
      setSaving(false);
    }
  };

  return (
    <Modal
      title={isEdit ? "تعديل الاختبار" : "اختبار جديد"}
      onClose={onClose}
      width={520}
      footer={
        <>
          <button className="gp-btn gp-btn-primary" onClick={submit} disabled={saving}>
            {saving ? "جارٍ الحفظ..." : isEdit ? "حفظ التعديلات" : "إنشاء الاختبار"}
          </button>
          <button className="gp-btn" onClick={onClose} disabled={saving}>
            إلغاء
          </button>
        </>
      }
    >
      {error && <div className="gp-error">{error}</div>}

      {!isEdit && (
        <p className="gp-muted" style={{ marginBottom: 12 }}>
          سيتم توليد رمز الاختبار وكلمة السر تلقائيًا بعد الإنشاء، وستجدهما في بطاقة الاختبار.
        </p>
      )}

      <div className="gp-field">
        <label htmlFor="qz-title">اسم الاختبار</label>
        <input id="qz-title" className="gp-input" value={form.title} onChange={set("title")} autoFocus />
      </div>
      <div className="gp-field" style={{ marginTop: 12 }}>
        <label htmlFor="qz-desc">الوصف (اختياري)</label>
        <textarea id="qz-desc" className="gp-input" rows={3} value={form.description} onChange={set("description")} />
      </div>

      <div style={{ display: "flex", gap: 12, marginTop: 12 }}>
        <div className="gp-field" style={{ flex: 1 }}>
          <label htmlFor="qz-duration">المدة (بالدقائق)</label>
          <input
            id="qz-duration"
            type="number"
            min={1}
            className="gp-input"
            value={form.duration_minutes}
            onChange={set("duration_minutes")}
            disabled={isEdit && quiz.attempts_count > 0}
          />
        </div>
        <div className="gp-field" style={{ flex: 1 }}>
          <label htmlFor="qz-count">عدد أسئلة كل طالب</label>
          <input
            id="qz-count"
            type="number"
            min={1}
            className="gp-input"
            placeholder="جميع الأسئلة"
            value={form.questions_per_attempt}
            onChange={set("questions_per_attempt")}
          />
        </div>
      </div>
      <p className="gp-muted" style={{ marginTop: 6 }}>
        إذا أدخلت عددًا أقل من أسئلة الاختبار، فسيحصل كل طالب على أسئلة عشوائية من الأسئلة المضافة.
      </p>

      <div style={{ display: "flex", gap: 12, marginTop: 12 }}>
        <div className="gp-field" style={{ flex: 1 }}>
          <label htmlFor="qz-attempts">عدد المحاولات المسموحة</label>
          <input
            id="qz-attempts"
            type="number"
            min={1}
            max={50}
            className="gp-input"
            value={form.max_attempts}
            onChange={set("max_attempts")}
          />
        </div>
        <div style={{ flex: 1 }} />
      </div>
      <p className="gp-muted" style={{ marginTop: 6 }}>
        يمكن للطالب إعادة الاختبار حتى هذا العدد، والدرجة المعتمدة هي أعلى درجة بين محاولاته.
      </p>

      <label style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 14, fontSize: 13.5 }}>
        <input type="checkbox" checked={form.is_open} onChange={set("is_open")} />
        متاح لأي طالب لديه الرمز أو الرابط (يُضاف إلى المجموعة بعد التسليم)
      </label>
      <label style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 10, fontSize: 13.5 }}>
        <input type="checkbox" checked={form.shuffle_questions} onChange={set("shuffle_questions")} />
        ترتيب الأسئلة عشوائي لكل طالب
      </label>
      <label style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 10, fontSize: 13.5 }}>
        <input type="checkbox" checked={form.show_answers_after_submit} onChange={set("show_answers_after_submit")} />
        إظهار الإجابات الصحيحة للطالب بعد انتهاء جميع محاولاته
      </label>
      <label style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 10, fontSize: 13.5 }}>
        <input type="checkbox" checked={form.requires_academic_id} onChange={set("requires_academic_id")} />
        طلب الرقم الأكاديمي من الطالب عند بدء كل محاولة
      </label>
    </Modal>
  );
}

/* ───────────────────────── أسئلة الاختبار ───────────────────────── */

const EMPTY_Q = {
  question_text: "",
  choice_a: "",
  choice_b: "",
  choice_c: "",
  choice_d: "",
  correct_answer: "A",
  points: 1,
  explanation: "",
};
const LETTERS = ["A", "B", "C", "D"];
const LETTER_AR = { A: "أ", B: "ب", C: "ج", D: "د" };

// صف سؤال في قائمة أسئلة الاختبار: نص منسّق + زر العين لعرض التفاصيل كاملة
function QuizQuestionRow({ q, index, locked, onRemove }) {
  const [open, setOpen] = useState(false);

  return (
    <div style={{ borderBottom: "1px solid #eee" }}>
      <div style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "8px 0" }}>
        <span className="gp-num">{index + 1}</span>
        <div style={{ flex: 1, minWidth: 0, fontSize: 13.5 }}>
          <RichText html={q.question_text} clamp={open ? undefined : 2} />
        </div>
        <span className="gp-badge ok">{LETTER_AR[q.correct_answer]}</span>
        {q.context_title && <span className="gp-badge">{q.context_title}</span>}
        <button
          type="button"
          className="gp-icon-btn"
          onClick={() => setOpen((o) => !o)}
          aria-label={open ? "إخفاء تفاصيل السؤال" : "عرض تفاصيل السؤال"}
          aria-expanded={open}
          title={open ? "إخفاء التفاصيل" : "عرض التفاصيل"}
        >
          {open ? <EyeOff size={15} /> : <Eye size={15} />}
        </button>
        {!locked && (
          <button className="gp-icon-btn danger" onClick={() => onRemove(q)} aria-label="حذف السؤال">
            <Trash2 size={14} />
          </button>
        )}
      </div>

      {open && (
        <QuestionDetails
          q={{
            ...q,
            choices: { A: q.choice_a, B: q.choice_b, C: q.choice_c, D: q.choice_d },
          }}
        />
      )}
    </div>
  );
}

function QuizQuestionsModal({ quiz, onClose, onChanged, notify }) {
  const locked = quiz.attempts_count > 0;
  const [questions, setQuestions] = useState(null);
  const [form, setForm] = useState(EMPTY_Q);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const res = await quizService.listQuestions(quiz.id);
      setQuestions(res.data);
    } catch (e) {
      setError(getErrorMessage(e));
      setQuestions([]);
    }
  }, [quiz.id]);

  useEffect(() => {
    load();
  }, [load]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const add = async () => {
    if (!form.question_text.trim()) return setError("أدخل نص السؤال");
    if (LETTERS.some((l) => !form[`choice_${l.toLowerCase()}`].trim())) return setError("أدخل الخيارات الأربعة");

    setSaving(true);
    setError("");
    try {
      await quizService.addQuestions(quiz.id, { ...form, points: Number(form.points) || 1 });
      setForm(EMPTY_Q);
      await load();
      onChanged();
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (q) => {
    try {
      await quizService.deleteQuestion(quiz.id, q.id);
      await load();
      onChanged();
    } catch (e) {
      notify(getErrorMessage(e), "err");
    }
  };

  const totalPoints = (questions || []).reduce((sum, q) => sum + (Number(q.points) || 0), 0);

  return (
    <Modal
      title={`أسئلة: ${quiz.title}`}
      onClose={onClose}
      width={720}
      footer={
        <button className="gp-btn" onClick={onClose}>
          إغلاق
        </button>
      }
    >
      {error && <div className="gp-error">{error}</div>}
      {locked && (
        <p className="gp-muted" style={{ marginBottom: 12 }}>
          بدأ بعض الطلاب هذا الاختبار، لذا لا يمكن إضافة أسئلة أو حذفها.
        </p>
      )}

      {questions !== null && (
        <div className="gp-actions" style={{ marginBottom: 12 }}>
          <span className="gp-badge ok" style={{ fontWeight: 700 }}>
            عدد الأسئلة: {questions.length}
          </span>
          <span className="gp-badge">مجموع الدرجات: {totalPoints}</span>
          {quiz.questions_per_attempt && (
            <span className="gp-badge">يحصل كل طالب على {quiz.questions_per_attempt} سؤالًا</span>
          )}
        </div>
      )}

      {questions === null ? (
        <Spinner />
      ) : questions.length === 0 ? (
        <p className="gp-muted">لا توجد أسئلة بعد. أضف أول سؤال من النموذج أدناه.</p>
      ) : (
        <div style={{ maxHeight: 360, overflowY: "auto", marginBottom: 16 }}>
          {questions.map((q, i) => (
            <QuizQuestionRow key={q.id} q={q} index={i} locked={locked} onRemove={remove} />
          ))}
        </div>
      )}

      {!locked && (
        <>
          <button className="gp-btn gp-btn-primary" style={{ marginBottom: 14 }} onClick={() => setImporting(true)}>
            استيراد من التطبيقات
          </button>
          <div style={{ fontWeight: 600, fontSize: 13.5, marginBottom: 10 }}>إضافة سؤال</div>
          <textarea
            className="gp-input"
            rows={2}
            placeholder="نص السؤال"
            value={form.question_text}
            onChange={set("question_text")}
          />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 8 }}>
            {LETTERS.map((l) => (
              <input
                key={l}
                className="gp-input"
                placeholder={`الخيار ${LETTER_AR[l]}`}
                value={form[`choice_${l.toLowerCase()}`]}
                onChange={set(`choice_${l.toLowerCase()}`)}
              />
            ))}
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            <select className="gp-select" value={form.correct_answer} onChange={set("correct_answer")} aria-label="الإجابة الصحيحة">
              {LETTERS.map((l) => (
                <option key={l} value={l}>
                  الإجابة الصحيحة: {LETTER_AR[l]}
                </option>
              ))}
            </select>
            <input
              type="number"
              min={1}
              className="gp-input"
              style={{ width: 90 }}
              value={form.points}
              onChange={set("points")}
              aria-label="الدرجة"
            />
          </div>
          <input
            className="gp-input"
            style={{ marginTop: 8 }}
            placeholder="شرح الإجابة (اختياري)"
            value={form.explanation}
            onChange={set("explanation")}
          />
          <button className="gp-btn gp-btn-primary" style={{ marginTop: 12 }} onClick={add} disabled={saving}>
            <Plus size={15} /> {saving ? "جارٍ الإضافة..." : "إضافة السؤال"}
          </button>
        </>
      )}
      {importing && (
        <QuizImportModal
          quiz={quiz}
          notify={notify}
          onClose={() => setImporting(false)}
          onImported={async () => {
            await load();
            onChanged();
          }}
        />
      )}
    </Modal>
  );
}

/* ───────────────────────── النتائج ───────────────────────── */

// صف طالب في نتائج الاختبار: الدرجة المعتمدة (الأعلى) + زر Reset + زر يفتح جميع محاولاته
function StudentResultRow({ s, maxAttempts, showAcademicId, onReset }) {
  const [open, setOpen] = useState(false);
  const attempts = s.attempts || [];

  return (
    <div style={{ borderBottom: "1px solid #eee" }}>
      <div style={{ display: "flex", gap: 10, alignItems: "center", padding: "10px 0" }}>
        <Avatar name={s.full_name} size={30} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <b style={{ display: "block", fontSize: 13.5 }}>{s.full_name}</b>
          {showAcademicId && (
            <small className="gp-muted" style={{ display: "block" }}>
              الرقم الأكاديمي: <span dir="ltr">{s.academic_id || "-"}</span>
            </small>
          )}
          <small className="gp-muted">
            {STATUS_LABELS[s.status]}
            {s.has_active_attempt && s.status === "SUBMITTED" && " · محاولة قيد التنفيذ"}
            {` · المحاولات ${s.attempts_used}/${maxAttempts}`}
            {s.submit_reason && ` · ${REASON_LABELS[s.submit_reason]}`}
            {s.submitted_at && ` · ${formatRelative(s.submitted_at)}`}
          </small>
        </div>
        {s.status === "SUBMITTED" ? (
          <span className="gp-num" style={{ fontWeight: 700 }}>
            {s.score}/{s.total_points} ({s.percentage}%)
          </span>
        ) : (
          <span className="gp-muted">-</span>
        )}
        {attempts.length > 0 && (
          <button
            type="button"
            className="gp-btn gp-btn-sm"
            onClick={() => onReset(s)}
            title="تصفير محاولات الطالب"
          >
            <RotateCcw size={13} /> Reset
          </button>
        )}
        {attempts.length > 0 && (
          <button
            type="button"
            className="gp-icon-btn"
            onClick={() => setOpen((o) => !o)}
            aria-expanded={open}
            aria-label={open ? "إخفاء المحاولات" : "عرض المحاولات"}
            title={open ? "إخفاء المحاولات" : "عرض جميع المحاولات"}
          >
            {open ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
          </button>
        )}
      </div>

      {open && (
        <div style={{ padding: "0 40px 10px" }}>
          {attempts.map((a) => (
            <div
              key={a.attempt_id}
              style={{ display: "flex", gap: 10, alignItems: "center", padding: "4px 0", fontSize: 13 }}
            >
              <span className="gp-num">{a.attempt_number}</span>
              <span className="gp-muted" style={{ flex: 1 }}>
                {STATUS_LABELS[a.status]}
                {showAcademicId && a.academic_id && ` · الرقم الأكاديمي: ${a.academic_id}`}
                {a.submit_reason && ` · ${REASON_LABELS[a.submit_reason]}`}
                {a.submitted_at && ` · ${formatRelative(a.submitted_at)}`}
              </span>
              {a.status === "SUBMITTED" ? (
                <span>
                  {a.score}/{a.total_points} ({a.percentage}%)
                </span>
              ) : (
                <span className="gp-muted">-</span>
              )}
              {s.status === "SUBMITTED" && a.attempt_number === s.attempt_number && (
                <span className="gp-badge ok">الأعلى</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function QuizResultsModal({ quiz, onClose, notify }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [exporting, setExporting] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetting, setResetting] = useState(false);

  const load = useCallback(() => {
    quizService
      .getResults(quiz.id)
      .then((res) => {
        setData(res.data);
        setError("");
      })
      .catch((e) => setError(getErrorMessage(e)));
  }, [quiz.id]);

  useEffect(() => {
    load();
  }, [load]);

  const handleExport = async () => {
    setExporting(true);
    try {
      await exportQuizResultsToExcel(quiz, data);
    } catch (e) {
      console.error(e);
      notify("تعذّر تصدير الملف، حاول مرة أخرى", "err");
    } finally {
      setExporting(false);
    }
  };

  const doReset = async () => {
    setResetting(true);
    try {
      const res = await quizService.resetStudent(quiz.id, resetTarget.student_id);
      notify(res.message || "تم تصفير المحاولات");
      setResetTarget(null);
      load();
    } catch (e) {
      notify(getErrorMessage(e), "err");
      setResetTarget(null);
    } finally {
      setResetting(false);
    }
  };

  return (
    <Modal
      title={`نتائج: ${quiz.title}`}
      onClose={onClose}
      width={720}
      footer={
        <>
          <button
            className="gp-btn gp-btn-primary"
            onClick={handleExport}
            disabled={!data || data.students.length === 0 || exporting}
          >
            <Download size={15} /> {exporting ? "جارٍ التصدير..." : "تصدير Excel"}
          </button>
          <button className="gp-btn" onClick={onClose}>
            إغلاق
          </button>
        </>
      }
    >
      {error && <div className="gp-error">{error}</div>}
      {!data && !error ? (
        <Spinner />
      ) : (
        data && (
          <>
            <div className="gp-actions" style={{ marginBottom: 14 }}>
              <span className="gp-badge">الطلاب: {data.assigned}</span>
              <span className="gp-badge ok">سلّموا: {data.submitted}</span>
              <span className="gp-badge">قيد التنفيذ: {data.in_progress}</span>
              <span className="gp-badge">لم يبدؤوا: {data.not_started}</span>
              <span className="gp-badge">المحاولات المسموحة: {data.max_attempts}</span>
              <span className="gp-badge">متوسط أعلى الدرجات: {data.average_percentage}%</span>
            </div>
            {data.students.length === 0 ? (
              <p className="gp-muted">لا يوجد طلاب بعد.</p>
            ) : (
              <div style={{ maxHeight: 380, overflowY: "auto" }}>
                {data.students.map((s) => (
                  <StudentResultRow
                    key={s.student_id}
                    s={s}
                    maxAttempts={data.max_attempts}
                    showAcademicId={!!data.quiz?.requires_academic_id}
                    onReset={setResetTarget}
                  />
                ))}
              </div>
            )}
          </>
        )
      )}

      {resetTarget && (
        <ConfirmDialog
          danger
          title="تصفير محاولات الطالب"
          message={`سيتم تصفير محاولات "${resetTarget.full_name}" في هذا الاختبار، ويمكنه البدء من جديد. المحاولات القديمة لن تظهر في النتائج.`}
          confirmLabel="تصفير"
          loading={resetting}
          onConfirm={doReset}
          onCancel={() => setResetTarget(null)}
        />
      )}
    </Modal>
  );
}

// المحاولة الأعلى لكل اختبار (بالنسبة المئوية، وعند التساوي الأقدم) — نفس قاعدة الخادم
function bestAttemptIds(rows) {
  const best = {};
  rows.forEach((r) => {
    const b = best[r.quiz_id];
    if (!b || r.percentage > b.percentage || (r.percentage === b.percentage && r.attempt_number < b.attempt_number)) {
      best[r.quiz_id] = r;
    }
  });
  return new Set(Object.values(best).map((r) => r.attempt_id));
}

export function StudentQuizResultsModal({ groupId, isAdmin, student, onClose }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let off = false;
    quizService
      .groupQuizResults(groupId, isAdmin, { student_id: student.id })
      .then((res) => !off && setRows(res.data))
      .catch((e) => !off && setError(getErrorMessage(e)));
    return () => {
      off = true;
    };
  }, [groupId, isAdmin, student.id]);

  const bestIds = rows ? bestAttemptIds(rows) : new Set();

  return (
    <Modal
      title={`اختبارات ${student.full_name}`}
      onClose={onClose}
      width={520}
      footer={
        <button className="gp-btn" onClick={onClose}>
          إغلاق
        </button>
      }
    >
      {error && <div className="gp-error">{error}</div>}
      {!rows && !error ? (
        <Spinner />
      ) : rows && rows.length === 0 ? (
        <p className="gp-muted">لم يسلّم هذا الطالب أي اختبار بعد.</p>
      ) : (
        rows && (
          <div style={{ maxHeight: 380, overflowY: "auto" }}>
            {rows.map((r) => (
              <div
                key={r.attempt_id}
                style={{ display: "flex", gap: 10, alignItems: "center", padding: "10px 0", borderBottom: "1px solid #eee" }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <b style={{ display: "block", fontSize: 13.5 }}>{r.quiz_title}</b>
                  <small className="gp-muted">
                    {r.max_attempts > 1 && `المحاولة ${r.attempt_number} من ${r.max_attempts} · `}
                    {REASON_LABELS[r.submit_reason]} · {formatRelative(r.submitted_at)} · أجاب عن {r.answered_count} من{" "}
                    {r.total_questions}
                  </small>
                </div>
                {r.max_attempts > 1 && bestIds.has(r.attempt_id) && <span className="gp-badge ok">الأعلى</span>}
                <span className="gp-num" style={{ fontWeight: 700 }}>
                  {r.score}/{r.total_points} ({r.percentage}%)
                </span>
              </div>
            ))}
          </div>
        )
      )}
    </Modal>
  );
}

/* ───────────────────────── تصدير الاختبار إلى مجموعات ───────────────────────── */

function QuizExportModal({ groupId, isAdmin, quiz, onClose, onDone }) {
  const [groups, setGroups] = useState(null);
  const [picked, setPicked] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let off = false;
    const fetchGroups = isAdmin ? groupService.adminListGroups : groupService.listGroups;
    fetchGroups({ page_size: 100 })
      .then((res) => !off && setGroups(res.data.filter((g) => g.id !== Number(groupId))))
      .catch((e) => {
        if (off) return;
        setError(getErrorMessage(e));
        setGroups([]);
      });
    return () => {
      off = true;
    };
  }, [groupId, isAdmin]);

  const toggle = (id) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const submit = async () => {
    setSaving(true);
    setError("");
    try {
      const res = await quizService.duplicate(quiz.id, [...picked]);
      onDone(res.message);
    } catch (e) {
      setError(getErrorMessage(e));
      setSaving(false);
    }
  };

  return (
    <Modal
      title={`تصدير: ${quiz.title}`}
      onClose={onClose}
      width={460}
      footer={
        <>
          <button className="gp-btn gp-btn-primary" onClick={submit} disabled={saving || picked.size === 0}>
            {saving ? "جارٍ التصدير..." : `تصدير${picked.size ? ` إلى ${picked.size} مجموعة` : ""}`}
          </button>
          <button className="gp-btn" onClick={onClose} disabled={saving}>
            إلغاء
          </button>
        </>
      }
    >
      {error && <div className="gp-error">{error}</div>}
      <p className="gp-muted" style={{ marginBottom: 12 }}>
        سيُنشأ اختبار جديد مستقل لكل مجموعة تختارها، بالإعدادات والأسئلة نفسها وبرمز وكلمة سر جديدين، ويُحفظ كمسودة.
        ولا تُنسخ النتائج ولا المحاولات.
      </p>
      {groups === null ? (
        <Spinner />
      ) : groups.length === 0 ? (
        <p className="gp-muted">لا توجد مجموعات أخرى للتصدير إليها.</p>
      ) : (
        <div className="gp-pick">
          {groups.map((g) => (
            <button key={g.id} type="button" aria-pressed={picked.has(g.id)} onClick={() => toggle(g.id)}>
              <span className="gp-who" style={{ display: "block", minWidth: 0 }}>
                <b>{g.name}</b>
                <small>
                  {g.students_count} طالب{isAdmin && g.teacher_name ? ` · ${g.teacher_name}` : ""}
                </small>
              </span>
            </button>
          ))}
        </div>
      )}
    </Modal>
  );
}

/* ───────────────────────── كلمة سر الاختبار ───────────────────────── */

// تظهر مخفية افتراضيًا (لأن الشاشة قد تكون مفتوحة أمام الطلاب)، مع إظهار ونسخ وتغيير
function PasswordChip({ password, onCopy, onRegenerate }) {
  const [shown, setShown] = useState(false);

  if (!password) return null;

  return (
    <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
      <span
        className="gp-badge"
        style={{ letterSpacing: 2, fontWeight: 700, fontFamily: "monospace", direction: "ltr" }}
        title="كلمة سر الاختبار"
      >
        <Lock size={12} style={{ marginInlineEnd: 4 }} />
        {shown ? password : "••••••••"}
      </span>
      <button
        type="button"
        className="gp-icon-btn"
        onClick={() => setShown((s) => !s)}
        aria-label={shown ? "إخفاء كلمة السر" : "إظهار كلمة السر"}
        aria-pressed={shown}
        title={shown ? "إخفاء كلمة السر" : "إظهار كلمة السر"}
      >
        {shown ? <EyeOff size={14} /> : <Eye size={14} />}
      </button>
      <button
        type="button"
        className="gp-icon-btn"
        onClick={() => onCopy(password, "تم نسخ كلمة السر")}
        aria-label="نسخ كلمة السر"
        title="نسخ كلمة السر"
      >
        <Copy size={14} />
      </button>
      <button
        type="button"
        className="gp-icon-btn"
        onClick={onRegenerate}
        aria-label="تغيير كلمة السر"
        title="تغيير كلمة السر"
      >
        <RefreshCw size={14} />
      </button>
    </div>
  );
}

/* ───────────────────────── التبويب ───────────────────────── */

export default function GroupQuizzesTab({ groupId, isAdmin, notify }) {
  const [quizzes, setQuizzes] = useState(null);
  const [error, setError] = useState("");
  const [modal, setModal] = useState(null); // {type: "form"|"questions"|"results"|"delete"|"export"|"password", quiz?}
  const [deleting, setDeleting] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await quizService.list({ group_id: groupId });
      setQuizzes(res.data);
      setError("");
    } catch (e) {
      setError(getErrorMessage(e));
      setQuizzes((prev) => prev ?? []);
    }
  }, [groupId]);

  useEffect(() => {
    setQuizzes(null);
    load();
  }, [load]);

  const copyText = async (text, okMsg) => {
    try {
      await navigator.clipboard.writeText(text);
      notify(okMsg);
    } catch {
      notify(text);
    }
  };

  const togglePublish = async (q) => {
    try {
      await quizService.update(q.id, { is_published: !q.is_published });
      notify(q.is_published ? "تم إيقاف نشر الاختبار" : "تم نشر الاختبار");
      load();
    } catch (e) {
      notify(getErrorMessage(e), "err");
    }
  };

  const doDelete = async () => {
    setDeleting(true);
    try {
      await quizService.remove(modal.quiz.id);
      notify("تم حذف الاختبار");
      setModal(null);
      load();
    } catch (e) {
      notify(getErrorMessage(e), "err");
      setModal(null);
    } finally {
      setDeleting(false);
    }
  };

  const doRegeneratePassword = async () => {
    setRegenerating(true);
    try {
      const res = await quizService.regeneratePassword(modal.quiz.id);
      notify(res?.message || "تم تغيير كلمة السر");
      setModal(null);
      load();
    } catch (e) {
      notify(getErrorMessage(e), "err");
      setModal(null);
    } finally {
      setRegenerating(false);
    }
  };

  const close = () => setModal(null);
  const createButton = (
    <button className="gp-btn gp-btn-primary" onClick={() => setModal({ type: "form" })}>
      <Plus size={15} /> اختبار جديد
    </button>
  );

  return (
    <>
      <div className="gp-toolbar" style={{ justifyContent: "flex-end" }}>
        {createButton}
      </div>

      {error && <div className="gp-error">{error}</div>}

      {quizzes === null ? (
        <Spinner />
      ) : quizzes.length === 0 ? (
        <div className="gp-panel">
          <EmptyState
            title="لا توجد اختبارات"
            hint="أنشئ اختبارًا للمجموعة، وأضف أسئلته، ثم انشره ليتمكن الطلاب من دخوله باستخدام الرمز وكلمة السر."
            action={createButton}
          />
        </div>
      ) : (
        quizzes.map((q) => (
          <div className="gp-panel" key={q.id} style={{ padding: 16, marginBottom: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
              <div style={{ minWidth: 0 }}>
                <b style={{ fontSize: 15 }}>{q.title}</b>
                {q.description && <p className="gp-muted" style={{ margin: "4px 0 0" }}>{q.description}</p>}
                <div className="gp-actions" style={{ marginTop: 10 }}>
                  <span className={`gp-badge ${q.is_published ? "ok" : ""}`}>{q.is_published ? "منشور" : "مسودة"}</span>
                  <span className="gp-badge">{q.duration_minutes} دقيقة</span>
                  <span className="gp-badge">
                    {q.questions_per_attempt
                      ? `${q.questions_per_attempt} من ${q.questions_count} سؤال`
                      : `${q.questions_count} سؤال`}
                  </span>
                  <span className="gp-badge">
                    {q.max_attempts} {q.max_attempts === 1 ? "محاولة مسموحة" : "محاولات مسموحة"}
                  </span>
                  <span className="gp-badge">{q.is_open ? "مفتوح للجميع" : "للمجموعة فقط"}</span>
                  {q.requires_academic_id && <span className="gp-badge">يتطلب الرقم الأكاديمي</span>}
                  <span className="gp-badge">{q.attempts_count} محاولة من الطلاب</span>
                </div>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "flex-start", alignSelf: "flex-start" }}>
                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    className="gp-btn gp-btn-sm"
                    onClick={() => copyText(q.code, "تم نسخ الرمز")}
                    title="نسخ الرمز"
                    style={{ letterSpacing: 2, fontWeight: 700 }}
                  >
                    <Copy size={13} /> {q.code}
                  </button>
                  <button
                    className="gp-btn gp-btn-sm"
                    onClick={() => copyText(q.share_url, "تم نسخ رابط المشاركة")}
                    disabled={!q.is_published}
                    title={q.is_published ? "نسخ رابط المشاركة" : "انشر الاختبار أولًا حتى يعمل الرابط"}
                  >
                    <Link2 size={13} /> رابط المشاركة
                  </button>
                </div>
                <PasswordChip
                  password={q.password}
                  onCopy={copyText}
                  onRegenerate={() => setModal({ type: "password", quiz: q })}
                />
              </div>
            </div>

            <div className="gp-actions" style={{ marginTop: 14 }}>
              <button className="gp-btn gp-btn-sm" onClick={() => setModal({ type: "questions", quiz: q })}>
                <ListChecks size={13} /> الأسئلة ({q.questions_count})
              </button>
              <button className="gp-btn gp-btn-sm" onClick={() => setModal({ type: "results", quiz: q })}>
                <BarChart3 size={13} /> النتائج
              </button>
              <button className="gp-btn gp-btn-sm" onClick={() => togglePublish(q)}>
                {q.is_published ? <EyeOff size={13} /> : <Eye size={13} />} {q.is_published ? "إيقاف النشر" : "نشر"}
              </button>
              <button className="gp-btn gp-btn-sm" onClick={() => setModal({ type: "form", quiz: q })}>
                <Pencil size={13} /> تعديل
              </button>
              <button className="gp-btn gp-btn-sm" onClick={() => setModal({ type: "export", quiz: q })}>
                <Share2 size={13} /> تصدير
              </button>
              <button className="gp-btn gp-btn-sm gp-btn-danger" onClick={() => setModal({ type: "delete", quiz: q })}>
                <Trash2 size={13} /> حذف
              </button>
            </div>
          </div>
        ))
      )}

      {modal?.type === "form" && (
        <QuizFormModal
          groupId={groupId}
          quiz={modal.quiz}
          onClose={close}
          onSaved={(saved, msg) => {
            const wasNew = !modal.quiz;
            close();
            notify(msg || "تم الحفظ بنجاح");
            load();
            // الاختبار الجديد تُفتح له نافذة الأسئلة مباشرة
            if (wasNew) setModal({ type: "questions", quiz: saved });
          }}
        />
      )}
      {modal?.type === "questions" && (
        <QuizQuestionsModal quiz={modal.quiz} onClose={close} onChanged={load} notify={notify} />
      )}
      {modal?.type === "results" && <QuizResultsModal quiz={modal.quiz} onClose={close} notify={notify} />}
      {modal?.type === "delete" && (
        <ConfirmDialog
          danger
          title="حذف الاختبار"
          message={`سيُحذف اختبار "${modal.quiz.title}" مع جميع أسئلته ونتائج الطلاب، ولا يمكن التراجع عن ذلك.`}
          confirmLabel="حذف الاختبار"
          loading={deleting}
          onConfirm={doDelete}
          onCancel={close}
        />
      )}
      {modal?.type === "password" && (
        <ConfirmDialog
          title="تغيير كلمة السر"
          message={`سيتم توليد كلمة سر جديدة لاختبار "${modal.quiz.title}". لن يتمكن الطلاب الذين يملكون كلمة السر القديمة من بدء محاولة جديدة بها، أما من بدأ محاولة بالفعل فيمكنه إكمالها.`}
          confirmLabel="تغيير كلمة السر"
          loading={regenerating}
          onConfirm={doRegeneratePassword}
          onCancel={close}
        />
      )}
      {modal?.type === "export" && (
        <QuizExportModal
          groupId={groupId}
          isAdmin={isAdmin}
          quiz={modal.quiz}
          onClose={close}
          onDone={(msg) => {
            close();
            notify(msg || "تم تصدير الاختبار");
          }}
        />
      )}
    </>
  );
}